import React, { useState } from 'react'
import { Col, Container, Row } from 'react-grid-system'
import { useForm } from 'react-hook-form'
import { TailSpin } from 'react-loader-spinner'

import Checkbox from '../../../../shared/components/Checkbox/Checkbox'
import Input from '../../../../shared/components/Input/Input'
import SuccessModal from '../../../../shared/components/SuccessModal/SuccessModal'
import { submitToZoho } from '../../../../utils/api'
import styles from './styles.module.scss'

// `hero`: the form opens its page (Contact), so it sits tight under the header.
const CSForm = ({ afterSubmit, hero = false }: { afterSubmit?: () => void; hero?: boolean }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoad, setIsLoad] = useState(false)

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
    getValues,
    setValue,
  } = useForm()
  const onSubmit = async () => {
    setIsLoad(true)
    const first = getValues('firstName')
    const last = getValues('lastName')
    const email = getValues('mail')
    const phone = getValues('phone')
    const orgName = getValues('org')
    const textArea = getValues('textArea')
    const service = getValues('service')

    const description = [textArea, service ? `Service of Interest: ${service}` : '']
      .filter(Boolean)
      .join('\n')

    try {
      await submitToZoho({
        company: orgName,
        firstName: first,
        lastName: last,
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
        ...(description ? { description } : {}),
      })
      reset()
      setIsModalOpen(true)
      afterSubmit?.()
    } finally {
      setIsLoad(false)
    }
  }

  return (
    <div className={`${styles.block}${hero ? ` ${styles.hero}` : ''}`}>
      <Container>
        <Row>
          <Col lg={6}>
            <div className={styles.leftContainer}>
              <h3>
                Get in touch with us to benefit from our comprehensive due diligence services.
              </h3>
              <br />
              <p>
                We are committed to providing prompt assistance, with a response to your inquiry
                within 24 hours.
              </p>
              <br />

              <p>Our consultants will promptly assess your needs and follow up accordingly.</p>
            </div>
          </Col>
          <Col lg={6} className={styles.rightContainer}>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void handleSubmit(onSubmit)()
              }}
            >
              <Container>
                <Row>
                  <Col sm={6} className={styles.left_col}>
                    <Input
                      name="firstName"
                      register={register}
                      rest={{
                        disabled: isLoad,
                        style: {
                          border: errors.firstName ? '1.5px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.firstName ? '#f2dbdbf0' : '#ffff',
                          margin: '10px 0',
                        },
                        placeholder: 'First Name',
                      }}
                    />
                  </Col>
                  <Col sm={6} className={styles.right_col}>
                    <Input
                      register={register}
                      name="lastName"
                      rest={{
                        disabled: isLoad,
                        style: {
                          margin: '10px 0',
                          border: errors.lastName ? '1.5px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.lastName ? '#f2dbdbf0' : '#ffff',
                        },
                        placeholder: 'Last name',
                      }}
                    />
                  </Col>
                  <Col sm={6} className={styles.left_col}>
                    <Input
                      register={register}
                      pattern={{
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'invalid email address',
                      }}
                      name="mail"
                      rest={{
                        disabled: isLoad,
                        style: {
                          margin: '10px 0',
                          border: errors.mail ? '1.5px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.mail ? '#f2dbdbf0' : '#ffff',
                        },
                        placeholder: 'Company Email',
                      }}
                    />
                  </Col>
                  <Col sm={6} className={styles.right_col}>
                    <Input
                      register={register}
                      name="phone"
                      required={false}
                      pattern={{
                        value: /^\+?[0-9\s().-]{7,}$/,
                        message: 'invalid phone',
                      }}
                      rest={{
                        disabled: isLoad,
                        style: {
                          margin: '10px 0',
                          border: errors.phone ? '1.5px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.phone ? '#f2dbdbf0' : '#ffff',
                        },
                        placeholder: 'Business Phone (opt.)',
                      }}
                    />
                  </Col>
                  <Col sm={6} className={styles.left_col}>
                    <Input
                      register={register}
                      name="org"
                      rest={{
                        disabled: isLoad,
                        style: {
                          margin: '10px 0',
                          border: errors.org ? '1.5px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.org ? '#f2dbdbf0' : '#ffff',
                        },
                        placeholder: 'Organisation',
                      }}
                    />
                  </Col>
                  <Col sm={6} className={styles.right_col}>
                    <Input
                      register={register}
                      name="service"
                      isDropdown
                      dropdownItems={[
                        'Integrity Due Diligence & Background Screening',
                        'Asset Tracing',
                        'Corporate Investigations',
                        'Strategic and Geopolitical Risk Advisory',
                        'Supply Chains and Sanctions Compliance',
                      ]}
                      onChange={(val: string) => {
                        setValue('service', val)
                      }}
                      rest={{
                        disabled: isLoad,
                        style: {
                          margin: '10px 0',
                          border: errors.service ? '1.5px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.service ? '#f2dbdbf0' : '#ffff',
                        },
                        placeholder: 'Service of Interest',
                      }}
                    />
                  </Col>
                  <Col sm={12}>
                    <Input
                      name="textArea"
                      register={register}
                      required={false}
                      rest={{
                        disabled: isLoad,
                        placeholder: 'How may we help you?',
                        style: {
                          margin: '10px 0',
                          border: '1px solid #E3D0C9',
                        },
                      }}
                    />
                  </Col>
                </Row>
                <Row className={styles.bottom_form_row}>
                  <Col>
                    <Checkbox
                      id={'chx-1'}
                      isError={!!errors.checkbox1}
                      register={register}
                      required={false}
                      name={'checkbox1'}
                      label={'Sign up for Urus Advisory Newsletter.'}
                    />
                    <br />
                    <Checkbox
                      id={'chx-2'}
                      isError={!!errors.checkbox2}
                      register={register}
                      name={'checkbox2'}
                      label={
                        <p>
                          Submitting this form confirms your agreement to Urus Advisory{' '}
                          <a href="/privacy" target="_blank">
                            Privacy Policy
                          </a>{' '}
                          and{' '}
                          <a href="/cookies" target="_blank">
                            Cookies Policy
                          </a>
                          .
                        </p>
                      }
                    />
                    <button>
                      SEND MESSAGE
                      {isLoad && (
                        <TailSpin
                          height="25"
                          width="25"
                          color="#fff"
                          ariaLabel="line-wave-loading"
                          wrapperClass={styles.loader}
                          wrapperStyle={{}}
                        />
                      )}
                    </button>
                  </Col>
                </Row>
              </Container>
            </form>
          </Col>
        </Row>
      </Container>
      <SuccessModal isOpen={isModalOpen} handleModal={(val: boolean) => setIsModalOpen(val)} />
    </div>
  )
}

export default CSForm
